import * as icons from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Stack } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { CrudFilters, LogicalFilter } from '@refinedev/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FilterRow, Primitive } from './FilterListRow.tsx';
import { FilterRowItem, getDefaultRow, operatorRequiresValue } from './FilterListRow.tsx';

const debounce = <P extends Array<unknown>>(callback: (...args: P) => void, ms: number) => {
  let timeoutId: number | null = null;
  return (...args: P) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => callback(...args), ms) as unknown as number;
  };
};

export type FilterListProps = {
  columns?: ReadonlyArray<GridColDef>;
  filters: CrudFilters;
  setFilters: (filters: CrudFilters, mode?: 'merge' | 'replace') => void;
  debounceMs?: number;
  defaultColumnName?: string;
};

const isLogicalFilter = (value: unknown): value is LogicalFilter => {
  if (!value || typeof value !== 'object') return false;
  return 'field' in value && 'operator' in value;
};

const isPrimitive = (value: unknown): value is Primitive => {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
};

const filtersToRows = (filters: CrudFilters, definitions: Map<string, GridColDef>): FilterRow[] => {
  const rows: FilterRow[] = [];

  for (const filter of filters) {
    if (!isLogicalFilter(filter)) continue;

    const definition = definitions.get(filter.field);
    if (!definition) continue;

    const type = definition.type ?? 'string';

    switch (type) {
      case 'string':
        switch (filter.operator) {
          case 'contains':
          case 'startswith':
          case 'endswith':
          case 'eq':
          case 'ne':
            if (isPrimitive(filter.value)) {
              rows.push({
                ...definition,
                type,
                operator: filter.operator,
                value: String(filter.value),
              });
            }
            break;
          case 'null':
          case 'nnull':
            rows.push({
              ...definition,
              type,
              operator: filter.operator,
              value: undefined,
            });
            break;
          default:
            break;
        }
        break;
      case 'number':
        switch (filter.operator) {
          case 'eq':
          case 'ne':
          case 'lt':
          case 'lte':
          case 'gt':
          case 'gte':
            if (isPrimitive(filter.value)) {
              rows.push({
                ...definition,
                type,
                operator: filter.operator,
                value: Number(filter.value),
              });
            }
            break;
          case 'null':
          case 'nnull':
            rows.push({
              ...definition,
              type,
              operator: filter.operator,
              value: undefined,
            });
            break;
        }
        break;
      case 'date':
      case 'dateTime':
        switch (filter.operator) {
          case 'lte':
          case 'gte':
            rows.push({
              ...definition,
              type,
              operator: filter.operator,
              value: new Date(filter.value),
            });
            break;
          case 'null':
          case 'nnull':
            rows.push({
              ...definition,
              type,
              operator: filter.operator,
              value: undefined,
            });
            break;
        }
        break;
      case 'boolean': {
        if (filter.operator === 'eq' && typeof filter.value === 'boolean') {
          rows.push({
            ...definition,
            type,
            operator: filter.operator,
            value: filter.value,
          });
        }
        break;
      }
      case 'singleSelect':
        switch (filter.operator) {
          case 'in':
            if (Array.isArray(filter.value)) {
              const values = filter.value.filter((item) => isPrimitive(item));
              rows.push({
                ...definition,
                type,
                operator: filter.operator,
                value: values,
              });
            }
            break;
          case 'null':
          case 'nnull':
            rows.push({
              ...definition,
              type,
              operator: filter.operator,
              value: undefined,
            });
            break;
          default:
            break;
        }
        break;
    }
  }

  return rows;
};

export const FilterList = ({
  columns: columns_,
  filters,
  setFilters,
  debounceMs = 0,
  defaultColumnName,
}: FilterListProps) => {
  const columns = useMemo(
    () =>
      columns_
        ?.filter((def) => def.filterable !== false && def.type !== 'actions')
        .map((def) => (def.type ? def : ({ ...def, type: 'string' } as const))) ?? [],
    [columns_],
  );

  const columnMap = useMemo(
    () => new Map(columns.map((column) => [column.field, column])),
    [columns],
  );

  const preservedFilters = useMemo(
    () => filters.filter((filter) => !isLogicalFilter(filter) || !columnMap.has(filter.field)),
    [filters, columnMap],
  );

  const preservedRef = useRef<CrudFilters>(preservedFilters);
  useEffect(() => {
    preservedRef.current = preservedFilters;
  }, [preservedFilters]);

  const toRows = useCallback(
    (sourceFilters: CrudFilters) => filtersToRows(sourceFilters, columnMap),
    [columnMap],
  );

  const [rows, setRows] = useState<FilterRow[]>(() => {
    const rows = toRows(filters);

    if (rows.length > 0) {
      return rows;
    }

    if (defaultColumnName) {
      const defaultField = columnMap.get(defaultColumnName);
      if (defaultField) {
        return [getDefaultRow(defaultField)];
      }
    }

    if (columns[0]) {
      return [getDefaultRow(columns[0])];
    }

    return [];
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const applyRows = useCallback(
    debounce((nextRows: FilterRow[]) => {
      const next: CrudFilters = [...preservedRef.current];

      for (const row of nextRows) {
        if (!operatorRequiresValue(row.operator)) {
          next.push({ field: row.field, operator: row.operator, value: '' });
          continue;
        }

        switch (row.type) {
          case 'string': {
            const raw =
              typeof row.value === 'string'
                ? row.value
                : row.value != null
                  ? String(row.value)
                  : '';
            const trimmed = raw.trim();
            if (trimmed.length === 0) continue;

            switch (row.operator) {
              case 'contains':
              case 'startswith':
              case 'endswith':
              case 'eq':
              case 'ne':
                next.push({ field: row.field, operator: row.operator, value: trimmed });
                break;
            }

            break;
          }
          case 'number':
            if (typeof row.value !== 'number') continue;
            switch (row.operator) {
              case 'eq':
              case 'ne':
              case 'lt':
              case 'lte':
              case 'gt':
              case 'gte':
                next.push({ field: row.field, operator: row.operator, value: row.value });
                break;
            }
            break;
          case 'date':
          case 'dateTime':
            if (!row.value) continue;
            switch (row.operator) {
              case 'lte':
              case 'gte':
                next.push({ field: row.field, operator: row.operator, value: row.value });
                break;
            }
            break;
          case 'boolean':
            next.push({ field: row.field, operator: row.operator, value: row.value });
            break;
          case 'singleSelect': {
            switch (row.operator) {
              case 'in': {
                const value = row.value ?? [];
                const values = Array.isArray(value) ? value : [value];
                if (values.length === 0) continue;
                next.push({ field: row.field, operator: row.operator, value: values });
                continue;
              }
            }
          }
        }
      }

      setFilters(next, 'replace');
    }, debounceMs),
    [setFilters],
  );

  const updateRows = useCallback(
    (updater: (prev: FilterRow[]) => FilterRow[]) => {
      setRows((prev) => {
        const next = updater(prev);
        applyRows(next);
        return next;
      });
    },
    [applyRows],
  );

  const clearAll = useCallback(() => {
    setRows([]);
    setFilters([...preservedRef.current], 'replace');
  }, [setFilters]);

  const addRow = useCallback(() => {
    const defaultField = columns[0];
    if (!defaultField) return;

    updateRows((prev) => [...prev, getDefaultRow(defaultField)]);
  }, [columns, updateRows]);

  if (columns.length === 0) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={1}>
        {rows.length === 0 ? (
          <Box sx={{ color: 'text.secondary', fontSize: 13, px: 1 }}>
            フィルタはまだ追加されていません
          </Box>
        ) : null}
        {rows.map((row, i) => {
          return (
            <FilterRowItem
              key={`${row.field}-${i}`}
              row={row}
              defs={columns}
              onUpdate={(nextRow) => {
                updateRows((prevRows) =>
                  prevRows.map((prevRow, j) => (i === j ? nextRow : prevRow)),
                );
              }}
              onDelete={() => {
                updateRows((prevRows) => prevRows.filter((_, j) => i !== j));
              }}
            />
          );
        })}
      </Stack>

      <Divider sx={{ mt: 1, mb: 1 }} />

      <Stack direction="row" justifyContent="space-between">
        <IconButton size="small" onClick={clearAll}>
          <icons.FilterListOffOutlined />
        </IconButton>
        <Button
          size="small"
          startIcon={<icons.AddOutlined />}
          onClick={addRow}
          disabled={columns.length === 0}
        >
          フィルタを追加
        </Button>
      </Stack>
    </Box>
  );
};
